import React, { useEffect, useRef, useState } from 'react';
import { Department } from '../../lib/types';

interface DashboardFilterBarProps {
  search:        string; setSearch:        (v: string) => void;
  filterStatus:  string; setFilterStatus:  (v: string) => void;
  filterDept:    string; setFilterDept:    (v: string) => void;
  filterCat:     string; setFilterCat:     (v: string) => void;
  filterDateFrom: string; setFilterDateFrom: (v: string) => void;
  filterDateTo:   string; setFilterDateTo:   (v: string) => void;
  departments: Department[];
  categories:  string[];
  onDownloadTemplate: () => void;
  onImport:    () => void;
  onAddOrder:  () => void;
}

const CaretIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" style={{ opacity: .55 }}>
    <path d="M2 4l4 4 4-4" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="var(--accent)" strokeWidth="2">
    <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Menu({
  value, options, onSelect,
}: { value: string; options: { value: string; label: string }[]; onSelect: (v: string) => void }) {
  return (
    <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 170 }}>
      {options.map(opt => (
        <button
          key={opt.value}
          className={`menu-item ${value === opt.value ? 'is-active' : ''}`}
          onClick={() => onSelect(opt.value)}
        >
          {value === opt.value ? <CheckIcon /> : <span style={{ width: 12 }} />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function DashboardFilterBar(props: DashboardFilterBarProps) {
  const [open, setOpen] = useState<null | 'status' | 'cat' | 'dept' | 'date' | 'csv'>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const activeDeptName = props.departments.find(d => d.id === props.filterDept)?.name || 'All departments';
  const statusOptions = [
    { value: 'all', label: 'All statuses' },
    { value: 'pending',   label: 'Pending' },
    { value: 'ordered',   label: 'Ordered' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'closed',    label: 'Closed' },
  ];
  const catOptions = [
    { value: 'all', label: 'All categories' },
    ...props.categories.map(c => ({ value: c, label: c })),
  ];

  return (
    <div
      ref={barRef}
      className="card flex items-center gap-2"
      style={{ padding: '8px 12px', marginBottom: 12, flexWrap: 'wrap' }}
    >
      {/* Search */}
      <div className="search-wrap" style={{ maxWidth: 180 }}>
        <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        <input
          className="search-inp"
          type="text"
          placeholder="Search..."
          value={props.search}
          onChange={e => props.setSearch(e.target.value)}
        />
      </div>

      {/* Status */}
      <div className="relative">
        <button
          className="filter-chip"
          onClick={() => setOpen(open === 'status' ? null : 'status')}
        >
          {props.filterStatus === 'all'
            ? 'All statuses'
            : props.filterStatus.charAt(0).toUpperCase() + props.filterStatus.slice(1)}
          <CaretIcon />
        </button>
        {open === 'status' && (
          <Menu
            value={props.filterStatus}
            options={statusOptions}
            onSelect={v => { props.setFilterStatus(v); setOpen(null); }}
          />
        )}
      </div>

      {/* Category */}
      <div className="relative">
        <button
          className="filter-chip"
          onClick={() => setOpen(open === 'cat' ? null : 'cat')}
        >
          {props.filterCat === 'all' ? 'All categories' : props.filterCat}
          <CaretIcon />
        </button>
        {open === 'cat' && (
          <Menu
            value={props.filterCat}
            options={catOptions}
            onSelect={v => { props.setFilterCat(v); setOpen(null); }}
          />
        )}
      </div>

      {/* Department */}
      <div className="relative">
        <button
          className="filter-chip"
          onClick={() => setOpen(open === 'dept' ? null : 'dept')}
        >
          {activeDeptName}
          <CaretIcon />
        </button>
        {open === 'dept' && (
          <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 180 }}>
            <button
              className={`menu-item ${props.filterDept === 'all' ? 'is-active' : ''}`}
              onClick={() => { props.setFilterDept('all'); setOpen(null); }}
            >
              {props.filterDept === 'all' ? <CheckIcon /> : <span style={{ width: 12 }} />}
              All departments
            </button>
            {props.departments.map(d => (
              <button
                key={d.id}
                className={`menu-item ${props.filterDept === d.id ? 'is-active' : ''}`}
                onClick={() => { props.setFilterDept(d.id); setOpen(null); }}
              >
                {props.filterDept === d.id ? <CheckIcon /> : <span style={{ width: 12 }} />}
                {d.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date range */}
      <div className="relative">
        <button
          className={`filter-chip ${(props.filterDateFrom || props.filterDateTo) ? 'is-active' : ''}`}
          onClick={() => setOpen(open === 'date' ? null : 'date')}
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
          {(props.filterDateFrom || props.filterDateTo)
            ? `${props.filterDateFrom || '...'} – ${props.filterDateTo || '...'}`
            : 'Dates'}
        </button>
        {open === 'date' && (
          <div
            className="menu flex flex-col gap-2"
            style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, padding: 12, minWidth: 240 }}
          >
            <div className="text-xs font-semibold text-muted">Filter by date range</div>
            <div className="flex items-center gap-2">
              <input
                className="input"
                type="date"
                value={props.filterDateFrom}
                onChange={e => props.setFilterDateFrom(e.target.value)}
                style={{ flex: 1, fontSize: 12 }}
              />
              <span className="text-xs text-subtle">to</span>
              <input
                className="input"
                type="date"
                value={props.filterDateTo}
                onChange={e => props.setFilterDateTo(e.target.value)}
                style={{ flex: 1, fontSize: 12 }}
              />
            </div>
            {(props.filterDateFrom || props.filterDateTo) && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { props.setFilterDateFrom(''); props.setFilterDateTo(''); }}
                style={{ alignSelf: 'flex-start' }}
              >Clear dates</button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* CSV menu */}
      <div className="relative">
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => setOpen(open === 'csv' ? null : 'csv')}
          title="Download template / Import CSV"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <circle cx="8" cy="3.5"  r="1.2" />
            <circle cx="8" cy="8"    r="1.2" />
            <circle cx="8" cy="12.5" r="1.2" />
          </svg>
        </button>
        {open === 'csv' && (
          <div className="menu" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, minWidth: 220 }}>
            <button
              className="menu-item"
              onClick={() => { props.onDownloadTemplate(); setOpen(null); }}
            >
              <div style={{ width: 28, height: 28, background: 'var(--blue-bg)', borderRadius: 'var(--r)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--blue)" strokeWidth="1.5">
                  <path d="M8 2v8M5 7l3 3 3-3" /><path d="M2 12h12" />
                </svg>
              </div>
              <div>
                <div className="font-medium">Download template</div>
                <div className="text-xs text-subtle">CSV format for bulk import</div>
              </div>
            </button>
            <div className="menu-sep" />
            <button
              className="menu-item"
              onClick={() => { props.onImport(); setOpen(null); }}
            >
              <div style={{ width: 28, height: 28, background: 'var(--accent-bg)', borderRadius: 'var(--r)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--accent)" strokeWidth="1.5">
                  <path d="M8 10V2M5 5l3-3 3 3" /><path d="M2 12h12" />
                </svg>
              </div>
              <div>
                <div className="font-medium">Import from CSV</div>
                <div className="text-xs text-subtle">Upload orders from a file</div>
              </div>
            </button>
          </div>
        )}
      </div>

      <button className="btn btn-primary btn-sm" onClick={props.onAddOrder}>
        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 3v10M3 8h10" />
        </svg>
        Add order
      </button>
    </div>
  );
}
